import { useState, useEffect, useMemo } from 'react';
import { api } from '../../services/api';
import type { AiUsageResponse, AiUsageModelData } from '../../services/api';
import { RefreshCw, Loader2, Cpu, AlertTriangle, Activity, TrendingUp, Users, XCircle } from 'lucide-react';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';

const CHART_COLORS = [
  '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16',
];

interface ModelInfo {
  model_id: string;
  display_name: string;
  model_type?: string;
}

interface PromptInfo {
  prompt_name: string;
  prompt_type: string;
}

export function AiUsage() {
  const [usageData, setUsageData] = useState<AiUsageResponse | null>(null);
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [_prompts, setPrompts] = useState<PromptInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [months, setMonths] = useState(1);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usageResult, modelsResult, promptsResult] = await Promise.all([
        api.getAiUsageData(months),
        api.listModels(),
        api.listPrompts({ prompt_state: 'prod' }),
      ]);
      setUsageData(usageResult);
      setModels(modelsResult.models || []);
      // Deduplicate prompts by name (they have versions)
      const seen = new Set<string>();
      const uniquePrompts: PromptInfo[] = [];
      for (const p of promptsResult.prompts || []) {
        if (!seen.has(p.prompt_name)) {
          seen.add(p.prompt_name);
          uniquePrompts.push({ prompt_name: p.prompt_name, prompt_type: p.prompt_type });
        }
      }
      setPrompts(uniquePrompts);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to load AI usage data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [months]);
  useAutoRefresh(loadData);

  // Build model display name lookup
  const modelNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const m of models) {
      map[m.model_id] = m.display_name;
      // Also map with "us." prefix which is how it's stored in usage
      if (!m.model_id.startsWith('us.')) {
        map[`us.${m.model_id}`] = m.display_name;
      }
    }
    return map;
  }, [models]);

  const getModelDisplayName = (modelId: string) => {
    return modelNames[modelId] || modelId.split('.').pop()?.replace(/-v\d.*$/, '') || modelId;
  };

  // Merge all daily data into a unified chart series for models
  const modelChartData = useMemo(() => {
    if (!usageData) return [];
    const dateMap: Record<string, Record<string, number>> = {};
    for (const [modelId, data] of Object.entries(usageData.models)) {
      for (const point of data.daily) {
        if (!dateMap[point.date]) dateMap[point.date] = {};
        dateMap[point.date][modelId] = point.calls;
      }
    }
    return Object.keys(dateMap).sort().map(date => ({
      date,
      label: formatDateLabel(date),
      ...dateMap[date],
    }));
  }, [usageData]);

  // Merge all daily data into a unified chart series for prompts (calls)
  const promptCallsChartData = useMemo(() => {
    if (!usageData) return [];
    const dateMap: Record<string, Record<string, number>> = {};
    for (const [promptName, data] of Object.entries(usageData.prompts)) {
      for (const point of data.daily) {
        if (!dateMap[point.date]) dateMap[point.date] = {};
        dateMap[point.date][promptName] = point.calls;
      }
    }
    return Object.keys(dateMap).sort().map(date => ({
      date,
      label: formatDateLabel(date),
      ...dateMap[date],
    }));
  }, [usageData]);

  // Prompt failures chart
  const promptFailuresChartData = useMemo(() => {
    if (!usageData) return [];
    const dateMap: Record<string, Record<string, number>> = {};
    for (const [promptName, data] of Object.entries(usageData.prompts)) {
      for (const point of data.daily) {
        if (point.failures && point.failures > 0) {
          if (!dateMap[point.date]) dateMap[point.date] = {};
          dateMap[point.date][promptName] = point.failures;
        }
      }
    }
    return Object.keys(dateMap).sort().map(date => ({
      date,
      label: formatDateLabel(date),
      ...dateMap[date],
    }));
  }, [usageData]);

  const hasData = usageData && (Object.keys(usageData.models).length > 0 || Object.keys(usageData.prompts).length > 0);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">AI Usage</h1>
          <p className="text-sm text-gray-500 mt-1">
            Tracking since April 9, 2026
            {usageData && ` | ${usageData.start_date} to ${usageData.end_date} (${usageData.days_in_range} days)`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
          >
            <option value={1}>Last 30 days</option>
            <option value={2}>Last 60 days</option>
            <option value={3}>Last 90 days</option>
            <option value={6}>Last 6 months</option>
            <option value={12}>Last 12 months</option>
          </select>
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}

      {loading && !usageData ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
        </div>
      ) : !hasData ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Activity className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-600 mb-2">No usage data yet</h2>
          <p className="text-gray-500">
            AI usage tracking started on April 9, 2026. Data will appear here as AI calls are made.
          </p>
        </div>
      ) : (
        <>
          {/* ==================== MODEL USAGE ==================== */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Cpu className="w-5 h-5" /> Usage by Model
            </h2>

            {/* Model summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {Object.entries(usageData!.models)
                .sort(([, a], [, b]) => b.total_calls - a.total_calls)
                .map(([modelId, data]) => (
                  <ModelCard
                    key={modelId}
                    modelId={modelId}
                    displayName={getModelDisplayName(modelId)}
                    data={data}
                    daysInRange={usageData!.days_in_range}
                  />
                ))}
            </div>

            {/* Model daily chart */}
            {modelChartData.length > 1 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-semibold text-gray-600 mb-4">Daily Calls by Model</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={modelChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip
                      labelFormatter={(_, payload) => payload?.[0]?.payload?.date || ''}
                      contentStyle={{ fontSize: 12 }}
                    />
                    <Legend
                      formatter={(value) => getModelDisplayName(value)}
                      wrapperStyle={{ fontSize: 11 }}
                    />
                    {Object.keys(usageData!.models).map((modelId, i) => (
                      <Line
                        key={modelId}
                        type="monotone"
                        dataKey={modelId}
                        stroke={CHART_COLORS[i % CHART_COLORS.length]}
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>

          {/* ==================== PROMPT USAGE ==================== */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" /> Usage by Prompt
            </h2>

            {/* Prompt summary table */}
            <div className="bg-white rounded-lg shadow mb-6 overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Prompt</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Total Calls</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Avg/Day</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Failures</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Fail Avg/Day</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Fail Rate</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Active Users</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(usageData!.prompts)
                    .sort(([, a], [, b]) => b.total_calls - a.total_calls)
                    .map(([promptName, data]) => {
                      const failRate = data.total_calls > 0
                        ? ((data.total_failures / data.total_calls) * 100).toFixed(1)
                        : '0.0';
                      return (
                        <tr key={promptName} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-4 font-mono text-sm">{promptName}</td>
                          <td className="py-2 px-4 text-right font-semibold">{data.total_calls.toLocaleString()}</td>
                          <td className="py-2 px-4 text-right text-gray-600">{data.avg_calls_per_day}</td>
                          <td className="py-2 px-4 text-right">
                            <span className={data.total_failures > 0 ? 'text-red-600 font-semibold' : 'text-gray-400'}>
                              {data.total_failures.toLocaleString()}
                            </span>
                          </td>
                          <td className="py-2 px-4 text-right text-gray-600">{data.avg_failures_per_day}</td>
                          <td className="py-2 px-4 text-right">
                            <span className={Number(failRate) > 5 ? 'text-red-600' : Number(failRate) > 0 ? 'text-amber-600' : 'text-green-600'}>
                              {failRate}%
                            </span>
                          </td>
                          <td className="py-2 px-4 text-right text-gray-600">{data.active_users}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {/* Prompt calls chart */}
            {promptCallsChartData.length > 1 && (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h3 className="text-sm font-semibold text-gray-600 mb-4">Daily Calls by Prompt</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={promptCallsChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip
                      labelFormatter={(_, payload) => payload?.[0]?.payload?.date || ''}
                      contentStyle={{ fontSize: 12 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {Object.keys(usageData!.prompts).map((name, i) => (
                      <Line
                        key={name}
                        type="monotone"
                        dataKey={name}
                        stroke={CHART_COLORS[i % CHART_COLORS.length]}
                        strokeWidth={1.5}
                        dot={false}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Prompt failures chart */}
            {promptFailuresChartData.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-semibold text-gray-600 mb-4 flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-400" /> Daily Validation Failures by Prompt
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={promptFailuresChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip
                      labelFormatter={(_, payload) => payload?.[0]?.payload?.date || ''}
                      contentStyle={{ fontSize: 12 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {Object.keys(usageData!.prompts)
                      .filter(name => usageData!.prompts[name].total_failures > 0)
                      .map((name, i) => (
                        <Line
                          key={name}
                          type="monotone"
                          dataKey={name}
                          stroke={CHART_COLORS[i % CHART_COLORS.length]}
                          strokeWidth={2}
                          dot={false}
                          connectNulls
                        />
                      ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}


/* ─── Sub-components ───────────────────────────────────────────── */

function ModelCard({
  modelId,
  displayName,
  data,
  daysInRange,
}: {
  modelId: string;
  displayName: string;
  data: AiUsageModelData;
  daysInRange: number;
}) {
  const monthlyEstimate = daysInRange >= 7
    ? Math.round((data.total_calls / daysInRange) * 30)
    : data.total_calls;
  const costPerUser = data.active_users > 0
    ? (monthlyEstimate * 0.003 / data.active_users).toFixed(2) // rough estimate
    : '—';

  return (
    <div className="bg-white rounded-lg shadow p-5">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-800 text-sm">{displayName}</h3>
          <p className="text-xs text-gray-400 font-mono truncate max-w-[200px]" title={modelId}>
            {modelId}
          </p>
        </div>
        <Cpu className="w-4 h-4 text-indigo-400 mt-0.5" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Stat label="Total calls" value={data.total_calls.toLocaleString()} />
        <Stat label="Est. monthly" value={monthlyEstimate.toLocaleString()} />
        <Stat label="Avg/day" value={String(data.avg_calls_per_day)} />
        <Stat label="Active users" value={String(data.active_users)} icon={<Users className="w-3 h-3 text-gray-400" />} />
        <Stat label="Est. $/user/mo" value={`$${costPerUser}`} />
      </div>
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-gray-500 flex items-center gap-1">{icon}{label}</div>
      <div className="text-lg font-bold text-gray-800">{value}</div>
    </div>
  );
}

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getMonth() + 1}/${d.getDate()}`;
}
